import { Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';

const photoFileName = 'photo.png';
const canvasMimeType = 'image/png';

@Component({
    selector: 'app-reactive-camera',
    standalone: false,
    templateUrl: './camera.component.html',
    styleUrls: ['./camera.component.scss']
})
export class ReactiveCameraComponent implements OnDestroy {
    @ViewChild('imageTag') public imageElement?: ElementRef<HTMLImageElement>;
    @ViewChild('videoTag') public videoElement?: ElementRef<HTMLVideoElement>;

    @Output() public sendCameraFile: EventEmitter<File> = new EventEmitter<File>();

    public photoIsDisplayed = false;
    public cameraIsVisible = false;
    public cameraIsLoading = false;
    public cameraError: string | null = null;

    private stream: MediaStream | null = null;

    public ngOnDestroy(): void {
        this.stopStream();
    }

    public openCamera(): void {
        this.cameraIsVisible = true;
        this.photoIsDisplayed = false;
        this.cameraError = null;
    }

    public closeCamera(): void {
        this.cameraIsVisible = false;
        this.stopStream();
    }

    /**
     * Starts video capture after the dialog content has been rendered.
     */
    public startCamera(): void {
        requestAnimationFrame(() => {
            void this.getStream();
        });
    }

    /**
     * Starts video capture from the camera.
     */
    public async getStream(): Promise<void> {
        this.photoIsDisplayed = false;
        this.cameraError = null;
        this.cameraIsLoading = true;
        this.stopStream();

        if (!navigator.mediaDevices?.getUserMedia) {
            this.cameraError = 'La cámara no está soportada por este navegador.';
            this.cameraIsLoading = false;
            return;
        }

        const constraints: MediaStreamConstraints = {
            video: {
                facingMode: { ideal: 'environment' }
            },
            audio: false
        };

        try {
            this.stream = await navigator.mediaDevices.getUserMedia(constraints);
            await this.bindStreamToVideo(this.stream);
        } catch (error) {
            this.cameraError = this.getCameraErrorMessage(error);
        } finally {
            this.cameraIsLoading = false;
        }
    }

    /**
     * Takes a photo from the active camera stream.
     */
    public async takePhoto(): Promise<boolean> {
        const video = this.videoElement?.nativeElement;

        if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
            this.cameraError = 'La cámara todavía no está preparada para capturar la imagen.';
            return false;
        }

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const canvasContext = canvas.getContext('2d');
        if (!canvasContext) {
            this.cameraError = 'No se ha podido preparar la captura de imagen.';
            return false;
        }

        canvasContext.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageBase64 = canvas.toDataURL(canvasMimeType);
        if (!imageBase64.startsWith(`data:${canvasMimeType};base64,`)) {
            this.cameraError = 'La imagen capturada no tiene un formato válido.';
            return false;
        }

        this.showCapturedImage(imageBase64);
        this.photoIsDisplayed = true;
        this.stopStream();

        try {
            const file = this.dataUrlToFile(imageBase64, photoFileName);
            this.sendCameraFile.emit(file);
            return true;
        } catch (error) {
            this.cameraError = this.getFileConversionErrorMessage(error);
            return false;
        }
    }

    public discardPhoto(): void {
        this.photoIsDisplayed = false;
        requestAnimationFrame(() => {
            void this.getStream();
        });
    }

    private async bindStreamToVideo(stream: MediaStream): Promise<void> {
        const video = this.videoElement?.nativeElement;

        if (!video) {
            this.cameraError = 'No se ha encontrado el visor de cámara.';
            return;
        }

        video.srcObject = stream;
        await video.play();
    }

    private showCapturedImage(imageBase64: string): void {
        requestAnimationFrame(() => {
            const image = this.imageElement?.nativeElement;

            if (image) {
                image.src = imageBase64;
            }
        });
    }

    private stopStream(): void {
        this.stream?.getTracks().forEach((track) => track.stop());
        this.stream = null;

        const video = this.videoElement?.nativeElement;
        if (video) {
            video.srcObject = null;
        }
    }

    private dataUrlToFile(dataUrl: string, filename: string): File {
        const parts = dataUrl.split(',');
        const header = parts[0] ?? '';
        const body = parts[1] ?? '';
        const mime = header.match(/:(.*?);/)?.[1] ?? '';
        const binary = atob(body);
        const bytes = new Uint8Array(binary.length);

        for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index);
        }

        return new File([bytes], filename, { type: mime });
    }

    private getCameraErrorMessage(error: unknown): string {
        if (error instanceof DOMException) {
            if (error.name === 'NotAllowedError') {
                return 'Permiso de cámara denegado. Habilítalo en la configuración del navegador.';
            }

            if (error.name === 'NotFoundError') {
                return 'No se encontró ninguna cámara conectada.';
            }

            if (error.name === 'AbortError') {
                return 'La cámara está en uso o no puede ser iniciada.';
            }
        }

        if (error instanceof Error && error.message.trim() !== '') {
            return `Error al iniciar la cámara: ${error.message}`;
        }

        return 'No se ha podido iniciar la cámara.';
    }

    private getFileConversionErrorMessage(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return `Error al convertir la imagen: ${error.message}`;
        }

        return 'No se ha podido convertir la captura en imagen.';
    }
}
